import { useEffect, useState } from "react";
import { apiQuery } from "app/game-data/useQuery";
import useResetableStorage from "app/util/useResetableStorage";

export const GAME_DATA_QUERY = /* GraphQL */ `
  query GAME_DATA($id: ID) {
    pixellence: nonograms {
      content(id: $id) {
        __typename
        id
        token
        publishDate
        level {
          constructorName
          difficulty
          id
          imageUrl
          name
          originalVideo
          originalScreenshotUrl
          puzzle
          size
        }
      }
    }
  }
`;

export interface GameData {
  id: number;
  publishDate: string;
  level: LevelData;
  shareImageUrl: string; // Not sure what this is?
  token: string; // Or this..
}

interface LevelData {
  constructorName: string; // The name of the person who designed the level
  difficulty: string;
  id: number;
  imageUrl: string; // The URL of the image that should appear when the level's complete
  name: string;
  puzzle: string;
  size: string;
  originalVideo: string;
  originalScreenshotUrl: string;
}

type GameDataQueryResponseType = {
  pixellence: {
    content: GameData;
  };
};

export const GAME_DATA_STORAGE_KEY = "PIXELLENCE_GAME_DATA";

export default function useFetchGameData(
  gameID?: number,
  storageKey = GAME_DATA_STORAGE_KEY,
) {
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useResetableStorage<GameData>(storageKey);

  // Check if we've already cached valid game data.
  const hasData = data?.id === gameID;

  useEffect(() => {
    if (!gameID || hasData) {
      return;
    }

    async function fetchData(id: number) {
      try {
        setLoading(true);
        const { data, error } = await apiQuery<
          GameDataQueryResponseType,
          { id: number }
        >(GAME_DATA_QUERY, {
          id,
        });
        if (!error && data?.pixellence.content?.id) {
          setData(data.pixellence.content);
        } else {
          throw error;
        }
      } catch {
        console.log("Could not fetch data");
      } finally {
        setLoading(false);
      }
    }

    fetchData(gameID);
  }, [gameID, data?.id, setData, hasData]);

  return { loading, data };
}
